<template>
    <bank-cards-store
        v-slot="{
            sid,
            store,
            data: card,
            error,
            is_loading: is_saving
        }"
        @success="onSuccess"
        @error="onError"
    >
        <base-form @submit="store(form, $refs.card_number.$refs.element._element)">
            <div class="mb-6 bg-white rounded shadow">
                <header class="flex items-center px-4 py-3 border-b sm:px-6 md:py-4">
                    <h1 class="text-lg font-normal">
                        Add a Bank Card
                    </h1>
                </header>

                <div class="px-6 pt-6 border-b">
                    <validator-alert :sid="sid" />

                    <div class="mb-6 text-grey-darker">
                        <p class="mb-3">
                            To begin using your card with Rentora, you'll need to confirm that you're the cardholder.
                        </p>

                        <p class="mb-3">
                            We'll make a £1 charge on your bank card. The charge will create a <strong>6-character code</strong> that can be
                            found on <strong>your statement</strong>. You'll need confirm your card by entering this code before you can use your card on Rentora.
                        </p>

                        <p>Once your card is confirmed, we will deduct any charges from your first payment(s) as a refund.</p>
                    </div>
                </div>

                <div class="px-6 pt-6 border-b">
                    <form-section>
                        <template slot="title">Bank Card</template>
                        <template slot="description">
                            The details on your card.
                        </template>

                        <grid-layout>
                            <grid-layout-item>
                                <form-item
                                    :sid="sid"
                                    :is-required="true"
                                    name="name"
                                >
                                    <template slot="label">Name On Card</template>

                                    <input-text
                                        v-model="form.name"
                                        :sid="sid"
                                        :disabled="is_saving"
                                        name="name"
                                    />
                                </form-item>
                            </grid-layout-item>

                            <grid-layout-item class="sm:w-1/2">
                                <form-item
                                    :sid="sid"
                                    :is-required="true"
                                    name="bank_card[number]"
                                    validator-name="bank_card"
                                >
                                    <template slot="label">Number</template>

                                    <div class="relative">
                                        <card-number
                                            ref="card_number"
                                            :stripe="$stripe"
                                            :options="stripe_options"
                                            :class="{
                                                'rounded bg-grey-bg': is_saving
                                            }"
                                        />

                                        <div
                                            v-if="is_saving"
                                            class="absolute inset-0 cursor-not-allowed"
                                        />
                                    </div>
                                </form-item>
                            </grid-layout-item>

                            <grid-layout-item class="sm:w-1/2">
                                <grid-layout>
                                    <grid-layout-item class="w-7/12">
                                        <form-item
                                            :sid="sid"
                                            :is-required="true"
                                            name="bank_card[expiry]"
                                        >
                                            <template slot="label">Expiry</template>

                                            <div class="relative">
                                                <card-expiry
                                                    :stripe="$stripe"
                                                    :options="stripe_options"
                                                    :class="{
                                                        'rounded bg-grey-bg': is_saving
                                                    }"
                                                />

                                                <div
                                                    v-if="is_saving"
                                                    class="absolute inset-0 cursor-not-allowed"
                                                />
                                            </div>
                                        </form-item>
                                    </grid-layout-item>

                                    <grid-layout-item class="w-5/12">
                                        <form-item
                                            :sid="sid"
                                            :is-required="true"
                                            name="bank_card[cvc]"
                                        >
                                            <template slot="label">CVC</template>

                                            <div class="relative">
                                                <card-cvc
                                                    :stripe="$stripe"
                                                    :options="stripe_options"
                                                    :class="{
                                                        'rounded bg-grey-bg': is_saving
                                                    }"
                                                />

                                                <div
                                                    v-if="is_saving"
                                                    class="absolute inset-0 cursor-not-allowed"
                                                />
                                            </div>
                                        </form-item>
                                    </grid-layout-item>
                                </grid-layout>
                            </grid-layout-item>
                        </grid-layout>
                    </form-section>
                </div>

                <div class="px-6 pt-6 border-b">
                    <form-section>
                        <template slot="title">Billing Address</template>
                        <template slot="description">
                            The current billing address for the card.
                        </template>

                        <grid-layout>
                            <grid-layout-item>
                                <form-items-address
                                    v-model="form.address"
                                    :sid="sid"
                                    :disabled="is_saving"
                                />
                            </grid-layout-item>
                        </grid-layout>
                    </form-section>
                </div>

                <div class="px-4 py-6 border-b sm:px-6">
                    <label class="flex cursor-pointer items-top md:pl-1/3">
                        <input-checkbox
                            v-model="has_confirmed"
                            class="flex-none mr-3"
                        />

                        <div class="text-sm">
                            I authorise Rentora to send instructions to the financial institution that issued my card
                            to take payments from my card account in accordance with the terms of my agreement with you
                            (i.e. to make rent payments), and to create a £1 charge for card verification purposes.
                        </div>
                    </label>
                </div>

                <base-form-footer class="rounded-b">
                    <base-button
                        :is-loading="is_saving"
                        :disabled="!has_confirmed"
                        class="w-full btn-blue md:w-auto"
                    >
                        Create Bank Card
                    </base-button>
                </base-form-footer>

                <error-modal
                    v-if="error && $error.is(error, { exclude: [422] })"
                    :error="error"
                />

                <modal-success :is-open="show_created_modal">
                    <template slot="heading">Bank card created!</template>

                    <p class="mb-3">
                        Your bank card has been added. Please confirm your card as soon as possible.
                    </p>

                    <template slot="footer">
                        <div class="flex flex-col md:flex-row-reverse">
                            <router-link
                                v-if="card"
                                :to="{
                                    name: $router.fix('dashboard.*.bank-cards.show'),
                                    params: {
                                        card: card.uuid
                                    }
                                }"
                                class="text-center btn btn-green"
                            >
                                Go to Bank Card
                            </router-link>
                            <router-link
                                :to="{ name: $router.fix('dashboard.*.settings.index') }"
                                class="mt-3 text-center btn md:mt-0 md:mr-3"
                            >
                                Go to Settings
                            </router-link>
                        </div>
                    </template>
                </modal-success>
            </div>
        </base-form>
    </bank-cards-store>
</template>

<script>
    import scroll from '../../../../support/scroll'
    import { CardNumber, CardExpiry, CardCvc } from 'vue-stripe-elements-plus'
    import BankCardsStore from '../../../../components/bank-cards-store'
    import FormItemsAddress from '../../../../components/form-items-address'

    export default {
        components: {
            CardNumber,
            CardExpiry,
            CardCvc,
            BankCardsStore,
            FormItemsAddress
        },
        data () {
            return {
                has_confirmed: false,
                show_created_modal: false,
                form: {
                    name: '',
                    address: {
                        street: '',
                        locality: '',
                        village: '',
                        town: '',
                        county: '',
                        postcode: ''
                    }
                }
            }
        },
        computed: {
            stripe_options () {
                return {
                    classes: {
                        base: 'input',
                        complete: 'is-complete',
                        empty: 'is-empty',
                        focus: 'is-focused',
                        invalid: 'is-invalid'
                    },
                    style: {
                        base: {
                            color: '#212934',
                            fontFamily: 'Nunito, sans-serif',
                            fontSize: '16px',
                            fontWeight: '400',
                            '::placeholder': {
                                color: '#CFD7E0'
                            },
                            ':disabled': {
                                backgroundColor: '#F00'
                            }
                        },
                        invalid: {
                            iconColor: '#f00',
                            color: '#1f2d3d'
                        }
                    }
                }
            }
        },
        created () {
            this.$stripe.elements({
                fonts: [
                    { cssSrc: 'https://fonts.googleapis.com/css?family=Nunito:400' }
                ]
            })
        },
        methods: {
            onSuccess () {
                setTimeout(() => {
                    this.show_created_modal = true
                }, 0)
            },
            onError () {
                scroll.toTop()
            }
        }
    }
</script>
